import {
  addDoc,
  collection,
  getDocs,
  getFirestore,
  limit,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import firebaseApp from "@/lib/firebase";
import type { School } from "@/types/school";
import { Course } from "@/types/course";
import { publicCall } from "@/lib/functionsApi";
import { courseRepository } from "./CourseRepository";
import { listRepository } from "./ListRepository";

/**
 * Repository class for School collection operations
 * Handles CRUD operations for schools in Firestore
 */
export class SchoolRepository {
  private db = getFirestore(firebaseApp);
  private collectionName = "schools" as const;

  // Create a new school
  async createSchool(
    userEmail: string,
    userId: string,
    name: string,
    slug: string
  ) {
    const schoolDoc = {
      name: name.trim(),
      slug: slug.trim(),
      createdAt: Timestamp.now(),
      creatorEmail: userEmail || null,
      creatorId: userId || null,
      isActive: true,
    };
    const schoolRef = await addDoc(
      collection(this.db, this.collectionName),
      schoolDoc
    );
    await listRepository.upsertSchoolCoursesList(schoolRef.id, []);
  }

  // Get school by slug
  async getSchoolBySlug(slug: string): Promise<School | null> {
    try {
      const result = (await publicCall("getSchoolBySlug", { slug })) as {
        school: School;
      };
      return result.school;
    } catch (error) {
      console.error("Error fetching school by slug:", error);
      return null;
    }
  }

  // Get school courses by school ID
  async getSchoolCourses(schoolId: string): Promise<Course[]> {
    const q = query(
      collection(this.db, "schoolCoursesList"),
      where("schoolId", "==", schoolId)
    );
    const snapshot = await getDocs(q);
    const allCoursesIds: string[] = [];
    snapshot.forEach((doc) => {
      console.log("School courses:", doc.id, "=>", doc.data());
      allCoursesIds.push(...doc.data().coursesIds);
    });
    const courses = await courseRepository.getByIds(allCoursesIds);
    return courses;
  }

  // Get user role in school
  async getUserRoleInSchool(
    schoolId: string,
    userEmail: string
  ): Promise<unknown> {
    try {
      const q = query(
        collection(this.db, "schoolMembers"),
        where("schoolId", "==", schoolId),
        where("email", "==", userEmail),
        limit(1)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        console.log("No matching documents found.");
        return null;
      }
      const result = snapshot.docs[0].data().role;
      return result;
    } catch (error) {
      console.error("Error fetching user role in school:", error);
      return null;
    }
  }
}

// Export singleton instance
export const schoolRepository = new SchoolRepository();
